import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  ActionListItem,
  Avatar,
  Box,
  Button,
  Highlight,
  Icon,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { ListItemAnimation } from 'components/Animations/ListItemAnimation'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { Customer, CustomerEntityType } from 'types/customers'

interface CustomersSearchListProps {
  customers: Customer[]
  isFetching?: boolean
  isFetchingNextPage?: boolean
  hasLoadedMore: boolean
  hasNextPage: boolean
  onCustomerSelect: (customer: Customer) => void
  onLoadMore: () => void
  setHasLoadedMore: React.Dispatch<React.SetStateAction<boolean>>
}

export const CustomersSearchList = ({
  customers,
  isFetching = false,
  isFetchingNextPage = false,
  hasLoadedMore,
  hasNextPage,
  onCustomerSelect,
  onLoadMore,
  setHasLoadedMore,
}: CustomersSearchListProps) => {
  const { t } = useTranslation()

  const listRef = useRef(null)
  const [customerList, setCustomerList] = useState<Customer[]>([])

  useEffect(() => {
    if (customers) {
      setCustomerList(customers)
    }
  }, [customers])

  useEffect(() => {
    if (listRef?.current && hasLoadedMore) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [customerList, hasLoadedMore])

  if (isFetching && !customers?.length) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (!isFetching && !customers?.length && !customerList?.length) {
    return (
      <Box className="flex items-center justify-center min-h-[200px]">
        <p className="text-sm text-foreground-subdued">{t('customers.noCustomersAvailable')}</p>
      </Box>
    )
  }

  return (
    <motion.ul ref={listRef} className="h-[calc(80vh-244px)] overflow-y-auto hide-scrollbar">
      <AnimatePresence>
        {customerList?.map((currentCustomer, index) => {
          const isLastTwo = customerList?.length - index <= 2
          const isNextToAnimate =
            customerList?.length - index > 2 && customerList?.length - index <= 10

          const customer = currentCustomer
          const isCompany = customer?.type === CustomerEntityType.COMPANY
          const isIndividual = customer?.type === CustomerEntityType.INDIVIDUAL

          const headerHighlightLeft = (isCompany || isIndividual) && (
            <Highlight
              iconElement={
                isCompany ? (
                  <Icon name="BuildingOffice" size="small" aria-label={t('contacts.company')} />
                ) : (
                  <Icon name="UserCircle" size="small" aria-label={t('contacts.individual')} />
                )
              }
              type="neutral"
              variant="emphasis"
            >
              {isCompany ? t('contacts.company') : t('contacts.individual')}
            </Highlight>
          )

          return (
            <ListItemAnimation
              key={customer?.reference}
              reference={customer?.reference}
              index={index}
              listLength={customerList?.length}
              hasLoadedMore={hasLoadedMore}
              isLastTwo={isLastTwo}
              isNextToAnimate={isNextToAnimate}
            >
              <ActionListItem
                key={customer?.reference}
                id={customer?.reference}
                title={customer?.name}
                testId={`customer-list-item-${index}`}
                HeaderHighlightLeft={headerHighlightLeft}
                Avatar={<Avatar type="entity" name={customer?.name ?? ''} />}
                BodyPre={
                  <p className="text-label text-foreground-prominent min-w-[0px] line-clamp-1">
                    {customer?.description}
                  </p>
                }
                onItemClick={() => onCustomerSelect(customer)}
              />
            </ListItemAnimation>
          )
        })}

        {isFetchingNextPage && (
          <Box className="mt-4">
            <RowsSkeletonLoader rows={2} />
          </Box>
        )}

        <Box flex justify="center" className="mt-2">
          <Button
            onClick={() => {
              onLoadMore()
              setHasLoadedMore(true)
            }}
            variant="ghost"
            disabled={!hasNextPage || isFetchingNextPage}
          >
            {hasNextPage ? t('customers.loadMore') : t('customers.noMoreCustomers')}
          </Button>
        </Box>
      </AnimatePresence>
    </motion.ul>
  )
}
